import { Toast } from "@techzyapp/ui-lib";
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  HttpStatusCode,
} from "axios";
import { useAuth } from "stores/auth";
import {
  authLocalStorage,
  getRefreshTokenFromLocalStorage,
  getTokenFromLocalStorage,
} from "utils/helpers/auth";

interface ApiConfig extends AxiosRequestConfig {
  baseURL: string;
}

const apiUrl = process.env.REACT_APP_API_URL || "";

const defaultConfig: ApiConfig = {
  baseURL: apiUrl,
};

type Subscriber = { resolve: () => void; reject: () => void };

let isRefreshing = false;
let refreshSubscribers: Subscriber[] = [];

const createApiClient = (config: ApiConfig): AxiosInstance => {
  const axiosInstance = axios.create(config);

  // Interceptor for adding the authorization header
  axiosInstance.interceptors.request.use((config) => {
    const token = getTokenFromLocalStorage();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  axiosInstance.interceptors.response.use(
    (res) => {
      return res;
    },
    async (err) => {
      // retry functionality
      if (
        !!err.config?.retryCount &&
        err.config?.retryCodes?.includes(err.response?.status)
      ) {
        err.config.retryCount -= 1;
        return axiosInstance(err.config);
      }

      // if (!err.response?.data?.infoMessage && err?.response?.status !== 401) {
      //   Toast.error(`Exception - ${err.message}`);
      // }

      // Check if the response status code is 401 (unauthorized) and the error message indicates token expiration
      const originalRequest = err.config;
      const keepMe = true;

      if (err.response.status === HttpStatusCode.Unauthorized && keepMe) {
        const retryOriginalRequest = new Promise((resolve) => {
          const resolveSubscriber = () => {
            resolve(axiosInstance(originalRequest));
          };
          const rejectSubscriber = () => {
            resolve(null);
          };
          refreshSubscribers.push({
            resolve: resolveSubscriber,
            reject: rejectSubscriber,
          });
        });

        if (!isRefreshing) {
          isRefreshing = true;

          try {
            const refreshToken = getRefreshTokenFromLocalStorage();
            // Make a request to the server to refresh the access token using the refresh token
            const response = await axios.post(
              defaultConfig.baseURL + "Identity/RefreshToken",
              { refreshToken },
              {
                baseURL: process.env.IDENTITY_URL,
                headers: {
                  Authorization: `Bearer ${getTokenFromLocalStorage()}`,
                },
              }
            );

            const newAccessToken = response.data.response.accessToken;
            const newRefreshToken = response.data.response.refreshToken;

            // Update the access token in the Axios instance and local storage
            axiosInstance.defaults.headers.common[
              "Authorization"
            ] = `Bearer ${newAccessToken}`;
            authLocalStorage({
              token: newAccessToken,
              refreshToken: newRefreshToken,
              keepMe: true,
            });

            // Execute all the pending requests
            refreshSubscribers.forEach((subscriber) => subscriber.resolve());

            refreshSubscribers = [];
          } catch (refreshError) {
            // Handle the refresh token request error, e.g., log out the user
            useAuth.getState().logout();

            refreshSubscribers.forEach((subscriber: Subscriber) =>
              subscriber.reject()
            );
            refreshSubscribers = [];
          } finally {
            isRefreshing = false;
          }
        }
        return retryOriginalRequest;
      }

      // if (!keepMe && err?.response?.status === HttpStatusCode.Unauthorized) {
      //   if (isClient()) {
      //     toasts.error('Session Expired', { toastId: 'toast-custom-id' })
      //     logoutEvent()
      //   }
      //   setAuthorizationToken('')
      // }

      return Promise.reject(err);
    }
  );

  // ... (Rest of the interceptor code)
  return axiosInstance;
};

const apiClient = createApiClient(defaultConfig);

type ApiBaseResponseWithData<TData> = {
  response: TData | null;
  totalCount: number | null;
} & ApiBaseResponse;

export type ApiBaseResponse = {
  isError: boolean;
  errorMessage: string | null;
};

//api request
const getData = async <T>(url: string, params?: AxiosRequestConfig) => {
  try {
    const response = await apiClient.get<ApiBaseResponseWithData<T>>(
      url,
      params
    );

    return {
      isError: false,
      errorMessage: null,
      response: response.data.response,
      totalCount: response.data.totalCount,
    };
  } catch (ex: unknown | AxiosError) {
    showApiError(ex);

    return {
      isError: true,
    };
  }
};

const postData = async <T>(
  url: string,
  data?: any,
  options?: { silent?: boolean }
) => {
  try {
    const response = await apiClient.post<ApiBaseResponseWithData<T>>(
      url,
      data
    );

    if (!options?.silent) Toast.success("Success");

    return {
      isError: false,
      errorCode: null,
      response: response.data.response,
    };
  } catch (ex: unknown | AxiosError) {
    showApiError(ex);
    return {
      isError: true,
    };
  }
};

const putData = async <T>(url: string, data?: any) => {
  try {
    const response = await apiClient.put<ApiBaseResponseWithData<T>>(url, data);

    Toast.success("Success");

    return {
      isError: false,
      errorCode: null,
      response: response.data.response,
    };
  } catch (ex: unknown | AxiosError) {
    showApiError(ex);
    return {
      isError: true,
    };
  }
};

const patchData = <T>(url: string, data?: any) =>
  apiClient.patch<ApiBaseResponseWithData<T>>(url, data);

const deleteData = async <T>(url: string) => {
  try {
    const response = await apiClient.delete<ApiBaseResponseWithData<T>>(url);

    Toast.success("Success");

    return {
      isError: false,
      errorCode: null,
      response: response.data.response,
    };
  } catch (ex: unknown | AxiosError) {
    showApiError(ex);
    return {
      isError: true,
    };
  }
};

const showApiError = (ex: unknown | AxiosError) => {
  let errorMessage = "Something Went Wrong";

  if (axios.isAxiosError(ex)) {
    errorMessage =
      ex.response?.data?.ErrorMessage ?? ex.response?.data?.errorMessage;
    if (errorMessage.includes(",")) {
      errorMessage = errorMessage
        ?.split(",")
        ?.map((o: string, i: number) => `${i + 1}.${o}`)
        .join("\r\n");
    }
  }

  if (axios.isAxiosError(ex) && !ex.response?.data?.response) {
    // TODO error dialog
  }

  Toast.error(errorMessage);
};

const apiManager = { getData, postData, putData, patchData, deleteData };

export default apiManager;
